/**
 * 微信/外链订单深链：pending 回跳、跳转页门禁、跨供应商切换
 */
import qs from "qs";
import { Message, MessageBox } from "element-ui";
import { getToken, setToken } from "@/assets/js/auth";

export const PENDING_ORDER_LINK_KEY = "ebk_pending_order_link";
export const ORDER_LINK_ROUTE_NAME = "orderLink";

export function savePendingOrderLink(route) {
    if (!route || !route.query || !route.query.o_id) {
        return;
    }
    try {
        sessionStorage.setItem(
            PENDING_ORDER_LINK_KEY,
            JSON.stringify({
                name: route.name || ORDER_LINK_ROUTE_NAME,
                query: {
                    o_id: String(route.query.o_id),
                    tu_id: route.query.tu_id
                        ? String(route.query.tu_id)
                        : "",
                },
            })
        );
    } catch (e) {
        /* ignore */
    }
}

export function consumePendingOrderLink() {
    try {
        const raw = sessionStorage.getItem(PENDING_ORDER_LINK_KEY);
        if (!raw) {
            return null;
        }
        sessionStorage.removeItem(PENDING_ORDER_LINK_KEY);
        return JSON.parse(raw);
    } catch (e) {
        sessionStorage.removeItem(PENDING_ORDER_LINK_KEY);
        return null;
    }
}

/** 读取 pending 但不删除（选供应商页展示订单所属提示用） */
export function getPendingOrderLink() {
    try {
        const raw = sessionStorage.getItem(PENDING_ORDER_LINK_KEY);
        if (!raw) {
            return null;
        }
        return JSON.parse(raw);
    } catch (e) {
        return null;
    }
}

/** 补充 pending 中的 tu_id、supplier_name 等（不删除 pending） */
export function patchPendingOrderLink(queryPatch) {
    if (!queryPatch) {
        return;
    }
    try {
        const pending = getPendingOrderLink();
        const base = pending || {
            name: ORDER_LINK_ROUTE_NAME,
            query: {},
        };
        base.query = Object.assign({}, base.query, queryPatch);
        if (base.query.o_id != null && base.query.o_id !== "") {
            base.query.o_id = String(base.query.o_id);
        }
        if (base.query.tu_id != null && base.query.tu_id !== "") {
            base.query.tu_id = String(base.query.tu_id);
        }
        sessionStorage.setItem(
            PENDING_ORDER_LINK_KEY,
            JSON.stringify(base)
        );
    } catch (e) {
        /* ignore */
    }
}

/** 深链已完成或放弃：清除 pending，避免下次登录/进系统再次弹跳转 */
export function clearPendingOrderLink() {
    try {
        sessionStorage.removeItem(PENDING_ORDER_LINK_KEY);
    } catch (e) {
        /* ignore */
    }
}

/**
 * 登录或选供应商成功后，若有 pending 则跳转订单跳转页
 */
export function redirectAfterAuth(router, defaultRoute) {
    const pending = consumePendingOrderLink();
    if (pending && pending.query && pending.query.o_id) {
        router.replace({
            name: pending.name || ORDER_LINK_ROUTE_NAME,
            query: Object.assign({}, pending.query, {
                _: String(Date.now()),
            }),
        });
        return true;
    }
    if (defaultRoute) {
        if (typeof defaultRoute === "string") {
            router.replace({ path: defaultRoute });
        } else {
            router.replace(defaultRoute);
        }
    }
    return false;
}

/**
 * 当前供应商与订单所属不一致时，确认后切换
 * @returns {Promise<boolean>}
 */
export function ensureSupplierForOrder(axios, ctx) {
    if (!ctx || !ctx.tu_id) {
        Message.error("无法识别订单所属供应商");
        return Promise.resolve(false);
    }

    let userInfo = {};
    try {
        const raw = getToken("userInfo");
        if (!raw) {
            Message.warning("请先登录");
            return Promise.resolve(false);
        }
        userInfo = JSON.parse(raw);
    } catch (e) {
        Message.warning("登录状态无效");
        return Promise.resolve(false);
    }

    const currentId =
        userInfo.id != null ? String(userInfo.id) : "";
    const targetId = String(ctx.tu_id);

    if (currentId === targetId) {
        return Promise.resolve(true);
    }

    const supplierName = ctx.supplier_name || "目标供应商";
    return MessageBox.confirm(
        `该订单属于【${supplierName}】，将切换到该供应商后查看订单详情。是否继续？`,
        "切换供应商",
        {
            confirmButtonText: "确定",
            cancelButtonText: "取消",
            type: "warning",
        }
    )
        .then(function () {
            return axios
                .supplierSwitchToTuId(qs.stringify({ tu_id: targetId }))
                .then(function (res) {
                    if (res && res.token) {
                        setToken("userInfo", JSON.stringify(res));
                        Message.success(
                            "已切换到「" + supplierName + "」"
                        );
                        return true;
                    }
                    Message.error("切换供应商失败");
                    return false;
                });
        })
        .catch(function () {
            return false;
        });
}

/**
 * 订单跳转页：登录态下校验供应商并进入详情
 * @returns {Promise<'detail'|'order'|'login'|'stay'>}
 */
export function runOrderLinkEntry(router, axios, query) {
    const oId =
        query && query.o_id != null ? String(query.o_id) : "";
    if (!oId) {
        Message.warning("缺少订单参数");
        router.replace({ path: "/order" });
        return Promise.resolve("order");
    }

    const userRaw = getToken("userInfo");
    if (!userRaw) {
        savePendingOrderLink({
            name: ORDER_LINK_ROUTE_NAME,
            query: { o_id: oId, tu_id: query.tu_id || "" },
        });
        router.replace({
            path: "/login",
            query: Object.assign({}, query, { o_id: oId }),
        });
        return Promise.resolve("login");
    }

    return axios
        .orderLinkContext(qs.stringify({ o_id: oId }))
        .then(function (ctx) {
            if (!ctx) {
                return "stay";
            }
            return ensureSupplierForOrder(axios, ctx).then(function (ok) {
                if (!ok) {
                    router.replace({ path: "/order" });
                    return "order";
                }
                router.replace({
                    name: "orderDetail",
                    query: {
                        o_id: oId,
                        _: String(Date.now()),
                    },
                });
                return "detail";
            });
        })
        .catch(function () {
            return "stay";
        });
}



// WEBPACK FOOTER //
// ./src/utils/orderDeepLink.js